/**
 * InternalWorkspace - Workspace Interno de DamyxIA
 * 
 * Este componente permite a los usuarios gestionar los "trabajos" internos
 * que DamyxIA puede crear, editar y abrir dentro de su propio Workspace.
 * 
 * Características:
 * - Lista de trabajos con cards
 * - Modal para crear nuevo trabajo
 * - Panel de edición/preview con UnifiedCodePreview
 * - Edición de nombre/descripción inline
 * - Integración con el chat para editar código
 * - Modal de código para ver/editar/exportar (modo esparto)
 */

import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import {
  Plus, X, ChevronRight, Loader2,
  Trash2, Sparkles, Rocket,
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { WorkspaceUpsellModal, shouldShowWorkspaceUpsell } from './WorkspaceUpsellModal';
import PremiumCTABanner from './PremiumCTABanner';
import usePremiumCTA from '../hooks/usePremiumCTA';
import SystemTemplatesPanel from './workspace/SystemTemplatesPanel';
import SampleDashboardsPanel from './workspace/SampleDashboardsPanel';
// 🟥 P26.40 · El Deploy del Workspace usa el motor de publicación real.
const DeployModal = lazy(() => import('./workspace/DeployModal'));
// 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — diff library para preview del auto-fix.
import { diffLines } from 'diff';
// 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Modal universal compartido.
import ExitConfirmModal from './system/ExitConfirmModal';
import axios from 'axios';

import { API_URL } from '../config';
import { registerWorkspaceApi, unregisterWorkspaceApi } from '../utils/workspaceApi';


// =====================================================
// SUBCOMPONENTES (extraídos al refactor 2026-02-08)
// =====================================================
import { CreateModuleModal, CANONICAL_BASE_TYPES } from './workspace/CreateModuleModal';
import { ChangeTypeModal } from './workspace/ChangeTypeModal';
import { ModuleDetailPanel } from './workspace/ModuleDetailPanel';
// Lazy: el CodeViewModal pesa 1044 LOC + jszip + lz-string + axios + UnifiedCodePreview.
// Solo se carga cuando el usuario realmente abre "Ver Código" — reduce el bundle
// inicial del Workspace y acelera el primer render. Se envuelve en <Suspense
// fallback={null}> abajo donde se renderiza el modal.
const CodeViewModal = lazy(() => import('./workspace/CodeViewModal'));

const InternalWorkspace = ({ onNavigateToChat }) => {
  const { language } = useLanguage();
  const [modules, setModules] = useState([]);
  const [quotaStatus, setQuotaStatus] = useState(null);

  // CUADRO NEGRO — Premium CTA Visual (ORB visual layer).
  // El hook lee /auth/me + /pro/usage y solo expone shouldShowPremiumCTA=true
  // cuando el usuario está enganchado (free + first_action_at + free_used>=10).
  // El cierre se persiste en sessionStorage para no reaparecer en la sesión.
  const { shouldShowPremiumCTA } = usePremiumCTA();
  const [ctaClosed, setCtaClosed] = useState(() => {
    try { return sessionStorage.getItem('damyxia.premium_cta.dismissed') === '1'; }
    catch { return false; }
  });
  const dismissPremiumCTA = useCallback(() => {
    setCtaClosed(true);
    try { sessionStorage.setItem('damyxia.premium_cta.dismissed', '1'); }
    catch { /* ignore */ }
  }, []);
  const [showLockedTypeModal, setShowLockedTypeModal] = useState(false);
  const [lockedTypeModalModule, setLockedTypeModalModule] = useState(null);
  // Upsell modal: aparece UNA vez por sesion por trigger en momentos de friccion.
  const [upsellTrigger, setUpsellTrigger] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedModule, setSelectedModule] = useState(null);
  const [showDeploy, setShowDeploy] = useState(false);  // 🟥 P26.40

  // 🟥 P26.40 · El botón Deploy que la app generada dibuja avisa desde el
  // iframe y abre la publicación REAL del proyecto seleccionado.
  useEffect(() => {
    const onDeployRequest = (ev) => {
      if (ev?.data?.type === 'DAMYX_DEPLOY_REQUEST') setShowDeploy(true);
    };
    window.addEventListener('message', onDeployRequest);
    return () => window.removeEventListener('message', onDeployRequest);
  }, []);  const [showCreateModal, setShowCreateModal] = useState(false);
  // 🟩 P26.23 · Limpieza masiva de módulos rotos (bug antiguo type:document).
  const [cleaningBroken, setCleaningBroken] = useState(false);
  // 🟥 CUADRO NEGRO 2026-06-02 — Panel "P de M" (Plantillas de Muestra).
  const [showTemplatesPanel, setShowTemplatesPanel] = useState(false);

  // 🟥 CUADRO NEGRO 2026-06-30 (Opción A) — Panel "Mis Trabajos".
  // 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — Auditoría Final IA / Health Check IA.
  // Flujo unificado: Lyon audita (JSON estructurado) → user decide Aceptar/Rechazar
  // → DamyxIA aplica fixes (preview) → diff visual + doble confirmación →
  // PUT del módulo → re-auditar para certificar → PERFECTO ⭐⭐⭐.
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditResult, setAuditResult] = useState(null); // structured response
  const [showAuditModal, setShowAuditModal] = useState(false);
  const [autofixLoading, setAutofixLoading] = useState(false);
  const [autofixPreview, setAutofixPreview] = useState(null); // { fixed_code, notes }
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [diffConfirmStep, setDiffConfirmStep] = useState(0); // 0 idle, 1 first-confirm, 2 applying
  const [certifiedPerfect, setCertifiedPerfect] = useState(false);
  // 🟥 CUADRO NEGRO 2026-06-03 — Panel "D de M" (Dashboards de Muestra).
  const [showSampleDashboards, setShowSampleDashboards] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [codeModalModule, setCodeModalModule] = useState(null);
  // 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Modal X universal en Workspace.
  const [showExitModal, setShowExitModal] = useState(false);
  // 🟥 CUADRO NEGRO 2026-06-30 (Copia editable) — tracking de módulos abiertos
  // como copia de una muestra. Los IDs viven solo en memoria (sesión) para
  // ofrecer Guardar/Descartar al intentar cerrar si hubo edición.
  //   • sampleCopyIdsRef       → Set con moduleIds marcados como copia.
  //   • sampleCopySnapshotsRef → Map moduleId → code inicial (para dirty check).
  //   • pendingCloseKind       → 'back' | 'workspace' | null. Guarda a dónde
  //     ir tras confirmar la salida (volver a lista vs cerrar el Workspace).
  const sampleCopyIdsRef = useRef(new Set());
  const sampleCopySnapshotsRef = useRef(new Map());
  const [pendingCloseKind, setPendingCloseKind] = useState(null);

  // 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — El splitter y el estado del
  // panel izquierdo (ancho, colapsado, drag) fueron ELIMINADOS con la lista.

  const t = language === 'es' ? {
    title: 'Área Interna de Trabajo',
    subtitle: 'Trabajos creados por DamyxIA',
    newWork: 'Nuevo Trabajo',
    search: 'Buscar trabajos...',
    empty: 'No hay trabajos internos',
    emptyHint: 'Crea tu primer trabajo usando el botón de arriba',
    loading: 'Cargando trabajos...',
    deleteConfirm: '¿Eliminar este trabajo?',
    deleted: 'Trabajo eliminado',
    created: 'Trabajo creado',
    updated: 'Trabajo actualizado',
    error: 'Error al procesar la solicitud'
  } : {
    title: 'Internal Workspace',
    subtitle: 'Works created by DamyxIA',
    newWork: 'New Work',
    search: 'Search works...',
    empty: 'No internal works',
    emptyHint: 'Create your first work using the button above',
    loading: 'Loading works...',
    deleteConfirm: 'Delete this work?',
    deleted: 'Work deleted',
    created: 'Work created',
    updated: 'Work updated',
    error: 'Error processing request'
  };

  // Cargar módulos
  const loadModules = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/damyxia/modules`);
      setModules(response.data.modules || []);
    } catch (error) {
      console.error('Error loading modules:', error);
      toast.error(t.error);
    } finally {
      setIsLoading(false);
    }
  }, [t.error]);

  // 🟥 P26.41 · WORKSPACE API REAL (`window.DamyxWorkspaceAPI`) — los 14
  // nombres exactos que exige el guardrail del Orchestrator. Se registran al
  // montar y se limpian al desmontar.
  useEffect(() => {
    const findModule = (id) => (modules || []).find((m) => m.id === id) || null;
    const openProject = async (projectId) => {
      if (!projectId) return null;
      let mod = findModule(projectId);
      if (!mod) {
        try {
          const res = await axios.get(`${API_URL}/api/damyxia/modules`);
          const list = res.data.modules || [];
          setModules(list);
          mod = list.find((m) => m.id === projectId) || null;
        } catch { /* silent */ }
      }
      if (mod) setSelectedModule(mod);
      return mod ? mod.id : null;
    };
    registerWorkspaceApi({
      getActiveProjectId: () => selectedModule?.id || null,
      getActiveItem: () => (selectedModule
        ? { id: selectedModule.id, type: selectedModule.detected_type || 'module' }
        : null),
      openProject,
      setActiveItem: (itemId) => openProject(itemId),
      compile: async (projectId) => {
        await openProject(projectId || selectedModule?.id);
        window.dispatchEvent(new CustomEvent('damyx:compile'));
        return true;
      },
      stopCompile: () => {
        window.dispatchEvent(new CustomEvent('damyx:stop-compile'));
        return true;
      },
      showPreview: async (projectId) => {
        const id = await openProject(projectId || selectedModule?.id);
        window.dispatchEvent(new CustomEvent('damyx:compile'));
        return id;
      },
      saveFile: async (fileId, content) => {
        const mod = selectedModule;
        if (!mod) return null;
        const files = Array.isArray(mod.files) ? mod.files.map((f) => ({ ...f })) : [];
        const idx = files.findIndex((f) => f.name === fileId || f.id === fileId);
        if (idx >= 0) files[idx].content = content;
        const body = idx >= 0 ? { files } : { code: content };
        const res = await axios.put(`${API_URL}/api/damyxia/modules/${mod.id}`, body);
        setSelectedModule(res.data);
        await loadModules();
        return res.data.id;
      },
      createModule: async (projectId, data = {}) => {
        const res = await axios.post(`${API_URL}/api/damyxia/modules`, {
          name: data.name || 'Nuevo módulo',
          description: data.description || '',
          type: 'custom',
          base_type: data.base_type || 'module',
          code: data.code || '',
          files: data.files || undefined,
        });
        await loadModules();
        return res.data.id;
      },
      createDocument: async (projectId, data = {}) => {
        const res = await axios.post(`${API_URL}/api/damyxia/modules`, {
          name: data.name || data.title || 'Nuevo documento',
          description: data.description || '',
          type: 'custom',
          base_type: 'document',
          code: data.content || data.code || '',
        });
        await loadModules();
        return res.data.id;
      },
      deleteItem: async (itemId) => {
        await axios.delete(`${API_URL}/api/damyxia/modules/${itemId}`);
        if (selectedModule?.id === itemId) { closeReasonRef.current = 'deleted'; setSelectedModule(null); }
        await loadModules();
        return true;
      },
      restoreItem: async (itemId) => {
        await axios.post(`${API_URL}/api/trash/restore`, { trash_id: itemId });
        await loadModules();
        return true;
      },
      publishProject: async (projectId) => {
        await openProject(projectId || selectedModule?.id);
        setShowDeploy(true);
        return true;
      },
      redeploy: async (projectId) => {
        await openProject(projectId || selectedModule?.id);
        setShowDeploy(true);
        return true;
      },
    });
    return () => unregisterWorkspaceApi();
  }, [modules, selectedModule, loadModules]);

  // 🟩 P26.23 + P26.29 · Contador de módulos REALMENTE rotos. Un documento
  // legítimo (blueprint, manual) NO es un residuo: solo cuentan los que
  // tienen JSON crudo del fence como código o están vacíos.
  const brokenDocsCount = useMemo(
    () => (modules || []).filter((m) => {
      if (m?.detected_type !== 'document') return false;
      const code = String(m?.code || '').trim();
      if (!code) return true;
      if (code.includes('damyxia-action')) return true;
      if (code.startsWith('{') && code.includes('"action"') && code.includes('"type"')) return true;
      return code.length < 40;
    }).length,
    [modules]
  );

  const handleCleanupBrokenDocs = useCallback(async () => {
    if (cleaningBroken || brokenDocsCount === 0) return;
    const msg = language === 'es'
      ? `¿Mover ${brokenDocsCount} módulo(s) rotos a la Papelera?\n\nSon residuos del bug antiguo (tipo:document). Podrás restaurarlos desde la Papelera si es necesario.`
      : `Move ${brokenDocsCount} broken module(s) to Trash?\n\nThese are residues from the old bug (type:document). You can restore them from Trash if needed.`;
    if (!window.confirm(msg)) return;
    setCleaningBroken(true);
    try {
      const r = await axios.post(`${API_URL}/api/damyxia/modules/cleanup/broken-documents`);
      const n = r?.data?.deleted_count ?? 0;
      toast.success(
        language === 'es'
          ? `Se limpiaron ${n} módulos rotos.`
          : `Cleaned ${n} broken modules.`
      );
      await loadModules();
    } catch (_e) {
      toast.error(
        language === 'es'
          ? 'Error al limpiar módulos rotos'
          : 'Error cleaning broken modules'
      );
    } finally {
      setCleaningBroken(false);
    }
  }, [cleaningBroken, brokenDocsCount, language, loadModules]);

  // 🟩 P26.25 · Limpieza profunda del workspace: escaneo + apply.
  //   Detecta empty_code, micro_stub, null_component, document_leftover.
  //   Protege módulos con tags, share, github, files[], render_mode=html.
  const handleDeepCleanup = useCallback(async () => {
    if (cleaningBroken) return;
    setCleaningBroken(true);
    try {
      // Dry-run primero para mostrar reporte
      const dry = await axios.post(`${API_URL}/api/damyxia/modules/cleanup/deep-scan`);
      const d = dry?.data || {};
      const cats = d.candidates || {};
      const total = Object.values(cats).reduce((a, b) => a + (b?.length || 0), 0);
      if (total === 0) {
        toast.success(
          language === 'es'
            ? `Sistema limpio: 0 candidatos / ${d.protected_count || 0} protegidos / ${d.total_scanned || 0} total.`
            : `System clean: 0 candidates / ${d.protected_count || 0} protected / ${d.total_scanned || 0} total.`
        );
        return;
      }
      const catLines = Object.entries(cats)
        .map(([k, v]) => `  · ${k}: ${v.length}`)
        .join('\n');
      const msg = language === 'es'
        ? `Se encontraron ${total} módulos rotos para limpiar:\n\n${catLines}\n\nProtegidos (NO se tocan): ${d.protected_count}\nTotal escaneado: ${d.total_scanned}\n\n¿Mover todos a la Papelera? (restaurables)`
        : `Found ${total} broken modules to clean:\n\n${catLines}\n\nProtected (untouched): ${d.protected_count}\nTotal scanned: ${d.total_scanned}\n\nMove all to Trash? (restorable)`;
      if (!window.confirm(msg)) return;
      const applied = await axios.post(`${API_URL}/api/damyxia/modules/cleanup/deep-scan?apply=true`);
      const n = applied?.data?.deleted_count ?? 0;
      toast.success(
        language === 'es'
          ? `Se limpiaron ${n} módulos rotos.`
          : `Cleaned ${n} broken modules.`
      );
      await loadModules();
    } catch (_e) {
      toast.error(
        language === 'es'
          ? 'Error al ejecutar limpieza profunda'
          : 'Error running deep cleanup'
      );
    } finally {
      setCleaningBroken(false);
    }
  }, [cleaningBroken, language, loadModules]);


  useEffect(() => {
    loadModules();
    // Cargar quota status del Workspace.
    (async () => {
      try {
        const qres = await axios.get(`${API_URL}/api/damyxia/modules/quota/status`);
        setQuotaStatus(qres.data);
      } catch (_) { /* silencioso — el usuario podria no estar logueado */ }
    })();
  }, [loadModules]);

  // FASE 11: Workspace → Chat sync.
  // Cada vez que cambia `selectedModule`, broadcast el event para que App.js
  // mande el active_module_id en cada llamada a /api/chat. moduleId=null
  // cuando el usuario deselecciona / cierra el módulo.
  // 🟥 CUADRO NEGRO MAESTRO (sec 6) — Ningún reset del proyecto activo sin
  // orden explícita. Cada deselección declara su motivo; los resets "auto"
  // (efectos, recargas de lista, acciones externas) quedan bloqueados.
  const closeReasonRef = useRef('auto');

  useEffect(() => {
    try {
      const reason = selectedModule?.id ? 'selected' : closeReasonRef.current;
      window.dispatchEvent(new CustomEvent('damyxia:active-module-changed', {
        detail: {
          moduleId: selectedModule?.id || null,
          moduleName: selectedModule?.name || null,
          source: 'workspace',
          reason,
        },
      }));
      closeReasonRef.current = 'auto';
    } catch (_) { /* noop */ }
  }, [selectedModule?.id]);

  // Escuchar el evento "damyxia-open-internal-module" disparado por
  // MessageBubble cuando el usuario aprieta "Enviar" en una respuesta de
  // visión: recargamos modulos y seleccionamos el recién creado.
  // 🟥 CUADRO NEGRO 2026-06-30 (Copia editable) — si el detail incluye
  // isSampleCopy=true, marcamos el moduleId como copia y guardamos el
  // snapshot del código inicial para detectar cambios al cerrar.
  useEffect(() => {
    const handler = async (e) => {
      const moduleId = e.detail?.moduleId;
      const isSampleCopy = !!e.detail?.isSampleCopy;
      if (!moduleId) return;
      try {
        const response = await axios.get(`${API_URL}/api/damyxia/modules`);
        const list = response.data.modules || [];
        setModules(list);
        const found = list.find((m) => m.id === moduleId);
        // 🟥 P26.45 · La lista viene sin `code`/`files`: hay que traer el
        // trabajo COMPLETO o el panel derecho queda en blanco.
        let full = found;
        try {
          const one = await axios.get(`${API_URL}/api/damyxia/modules/${moduleId}`);
          full = one.data?.module || one.data || found;
        } catch (_) { /* se usa el de la lista */ }
        if (full) {
          if (isSampleCopy) {
            sampleCopyIdsRef.current.add(full.id);
            sampleCopySnapshotsRef.current.set(full.id, full.code || '');
          }
          setSelectedModule(full);
        }
      } catch (err) {
        console.error('Error refreshing modules after auto-create:', err);
      }
    };
    window.addEventListener('damyxia-open-internal-module', handler);
    return () => window.removeEventListener('damyxia-open-internal-module', handler);
  }, []);

  // FASE 11: Chat → Workspace sync.
  // Cuando el chat MODIFICA el módulo activo (response.internal_module_action='editing'
  // con el mismo ID), App.js dispatcha 'damyxia:internal-module-updated' con el ID.
  // Workspace re-fetcha el módulo y actualiza la vista en vivo.
  useEffect(() => {
    const handler = async (e) => {
      const moduleId = e.detail?.moduleId;
      if (!moduleId) return;
      // Snapshot del updated_at previo para detectar si el backend ya
      // materializó el cambio del turno actual del chat.
      const prevUpdatedAt = selectedModule?.id === moduleId ? selectedModule?.updated_at : null;

      const fetchOnce = async () => {
        const [listResp, oneResp] = await Promise.all([
          axios.get(`${API_URL}/api/damyxia/modules`),
          axios.get(`${API_URL}/api/damyxia/modules/${moduleId}`),
        ]);
        return {
          list: listResp.data.modules || [],
          full: oneResp.data?.module || oneResp.data,
        };
      };

      try {
        // 🟥 FIX persistencia turno #N (2026-02-09):
        // 1) El endpoint DETAIL es la fuente autoritativa (no LIST).
        // 2) Race-condition guard: reintentar hasta 3 veces si el backend
        //    todavía no reflejó el nuevo updated_at (el evento del chat
        //    puede ganar la carrera contra el commit en Mongo por muy
        //    pocos ms). Sin este retry, veíamos la versión previa y
        //    sobreescribíamos el estado local, perdiendo el turno 2.
        let attempt = 0;
        let result = await fetchOnce();
        while (
          attempt < 3 &&
          prevUpdatedAt &&
          result.full?.updated_at &&
          result.full.updated_at === prevUpdatedAt
        ) {
          await new Promise((r) => setTimeout(r, 250));
          attempt += 1;
          result = await fetchOnce();
        }
        setModules(result.list);
        if (result.full && selectedModule?.id === moduleId) {
          setSelectedModule(result.full);
        }
      } catch (err) {
        console.error('Error refreshing module after chat edit:', err);
      }
    };
    window.addEventListener('damyxia:internal-module-updated', handler);
    return () => window.removeEventListener('damyxia:internal-module-updated', handler);
  }, [selectedModule?.id, selectedModule?.updated_at]);

  // 🟥 CUADRO NEGRO V4 — Refresco tras Push/Pull desde GitHub.
  // ProjectTreeViewer dispara `damyxia-reload-internal-module` con el id.
  // Re-fetcheamos la lista y refrescamos el módulo seleccionado.
  useEffect(() => {
    const handler = async (e) => {
      const moduleId = e.detail?.moduleId;
      if (!moduleId) return;
      try {
        const response = await axios.get(`${API_URL}/api/damyxia/modules`);
        const list = response.data.modules || [];
        setModules(list);
        const found = list.find((m) => m.id === moduleId);
        if (found && selectedModule?.id === moduleId) {
          setSelectedModule(found);
        }
      } catch (err) {
        console.error('Error refreshing module after GitHub sync:', err);
      }
    };
    window.addEventListener('damyxia-reload-internal-module', handler);
    return () => window.removeEventListener('damyxia-reload-internal-module', handler);
  }, [selectedModule?.id]);

  // 🟥 CUADRO NEGRO 2026-07-02 — Auto-Visual Creation (Fase 2 FULL).
  // Al montar el Workspace, si sessionStorage tiene payload AVC (dejado por
  // AutoVisualCreationModal), creamos un módulo nuevo con el código precargado
  // y lo dejamos seleccionado.
  useEffect(() => {
    const raw = (() => {
      try { return sessionStorage.getItem('__damyxia_avc__'); }
      catch { return null; }
    })();
    if (!raw) return;
    let payload;
    try { payload = JSON.parse(raw); }
    catch { sessionStorage.removeItem('__damyxia_avc__'); return; }
    if (!payload || !payload.code || !payload.verb_id) {
      sessionStorage.removeItem('__damyxia_avc__');
      return;
    }
    // Limpiamos ANTES del await para evitar doble-creación en re-renders.
    try { sessionStorage.removeItem('__damyxia_avc__'); } catch { /* noop */ }

    (async () => {
      try {
        const now = new Date();
        const stamp = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
        const name = `${payload.verb_label || payload.verb_id} · ${stamp}`;
        // 🟥 CUADRO NEGRO — El schema ModuleCreate del backend acepta:
        //   type ∈ {'dashboard','table','custom'}, base_type canonical ∈
        //   {'Dashboard','Tienda','Landing','Sitio Web','Panel'} y `code`
        //   como opcional. Pasamos el code en el POST inicial para evitar
        //   una segunda request PUT.
        const response = await axios.post(`${API_URL}/api/damyxia/modules`, {
          name,
          type: 'custom',
          base_type: 'Panel',
          description: payload.user_text || `Auto-creado desde verbo "${payload.verb_id}" (${payload.source}).`,
          code: payload.code,
          // 🟥 CUADRO NEGRO 2026-07-05 — Adjunta conversation_id activo para log de reconstrucción.
          conversation_id: (typeof window !== 'undefined' && window.__damyxia_active_conversation_id) || undefined,
        });
        const newModule = response.data;
        setModules(prev => [...prev, newModule]);
        setSelectedModule(newModule);
        toast.success(
          payload.source === 'ai'
            ? `Módulo "${name}" creado con código IA`
            : `Módulo "${name}" creado desde skeleton`,
          { duration: 4000 }
        );
      } catch (err) {
        console.error('[AVC] error creando módulo:', err);
        const msg = err?.response?.data?.detail?.message
          || err?.response?.data?.detail
          || 'No pudimos crear el módulo automáticamente.';
        toast.error(typeof msg === 'string' ? msg : 'No pudimos crear el módulo automáticamente.');
      }
    })();
    // Solo al montar — sessionStorage ya se limpió arriba.
  }, []);
  
  // NO auto-seleccionar módulos - el usuario debe elegir manualmente

  // Crear módulo
  const handleCreate = async ({ name, type, base_type, description }) => {
    try {
      const response = await axios.post(`${API_URL}/api/damyxia/modules`, {
        name,
        type,
        base_type,
        description,
        // 🟥 CUADRO NEGRO 2026-07-05 — Log de reconstrucción por conversación.
        conversation_id: (typeof window !== 'undefined' && window.__damyxia_active_conversation_id) || undefined,
      });
      setModules(prev => [...prev, response.data]);
      setSelectedModule(response.data);
      // Refrescar quota local.
      try {
        const qres = await axios.get(`${API_URL}/api/damyxia/modules/quota/status`);
        setQuotaStatus(qres.data);
      } catch (_) { /* noop */ }
      toast.success(t.created);
    } catch (error) {
      console.error('Error creating module:', error);
      // Bloqueo de cuota free.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 403 && detail?.error === 'free_quota_exceeded') {
        toast.error(detail.message || t.error);
        if (shouldShowWorkspaceUpsell('quota_full')) {
          setUpsellTrigger('quota_full');
        }
      } else {
        toast.error(t.error);
      }
      throw error;
    }
  };

  // Cambiar tipo base de un Trabajo (regla 4 — solo via boton explicito).
  const handleChangeType = async (moduleId, newBaseType) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/damyxia/modules/${moduleId}/change-type`,
        { new_base_type: newBaseType }
      );
      setModules(prev => prev.map(m => m.id === moduleId ? response.data : m));
      if (selectedModule?.id === moduleId) {
        setSelectedModule(response.data);
      }
      // Refrescar quota.
      try {
        const qres = await axios.get(`${API_URL}/api/damyxia/modules/quota/status`);
        setQuotaStatus(qres.data);
      } catch (_) { /* noop */ }
      toast.success(language === 'es' ? 'Tipo cambiado correctamente' : 'Type changed');
      return response.data;
    } catch (error) {
      console.error('Error changing type:', error);
      const detail = error?.response?.data?.detail;
      const code = error?.response?.status;
      if (code === 403) {
        toast.error(detail?.message || (language === 'es' ? 'No puedes cambiar el tipo' : 'Cannot change type'));
        const errKind = detail?.error;
        const upsell = errKind === 'type_locked'
          ? 'type_locked'
          : (errKind === 'free_quota_exceeded' ? 'quota_full' : null);
        if (upsell && shouldShowWorkspaceUpsell(upsell)) {
          setUpsellTrigger(upsell);
        }
      } else {
        toast.error(language === 'es' ? 'Error al cambiar tipo' : 'Failed to change type');
      }
      throw error;
    }
  };

  // 🟩 P26.30 · Plantillas rápidas: crea el proyecto y lo abre al instante.
  const [creatingTemplate, setCreatingTemplate] = useState(null);
  const handleQuickTemplate = async (templateKey) => {
    if (creatingTemplate) return;
    setCreatingTemplate(templateKey);
    try {
      const res = await axios.post(`${API_URL}/api/damyxia/modules/templates/quick`, {
        template: templateKey,
      });
      const newId = res.data?.module_id;
      const listRes = await axios.get(`${API_URL}/api/damyxia/modules`);
      const list = listRes.data.modules || [];
      setModules(list);
      const created = list.find((m) => m.id === newId);
      if (created) setSelectedModule(created);
      toast.success(language === 'es'
        ? `Listo — "${res.data?.name}" creado`
        : `Done — "${res.data?.name}" created`);
    } catch {
      toast.error(language === 'es' ? 'No se pudo crear la plantilla' : 'Could not create template');
    } finally {
      setCreatingTemplate(null);
    }
  };

  // Actualizar módulo
  const handleUpdate = async (moduleId, updates) => {    // 🟥 CUADRO NEGRO V2 — Sync local sin pegarle al backend (usado por
    // ProjectTreeViewer cuando PUT /workspace/projects/{id}/files ya
    // persistió). Solo refrescamos memoria para mantener la UI consistente.
    if (updates && updates.__localOnly) {
      const { __localOnly, ...patch } = updates;
      setModules(prev => prev.map(m => m.id === moduleId ? { ...m, ...patch } : m));
      if (selectedModule?.id === moduleId) {
        setSelectedModule(prev => prev ? { ...prev, ...patch } : prev);
      }
      return { ...selectedModule, ...patch };
    }
    try {
      const payload = { ...updates };
      // 🟥 CUADRO NEGRO 2026-07-05 — Log de reconstrucción por conversación.
      const convId = (typeof window !== 'undefined' && window.__damyxia_active_conversation_id) || undefined;
      if (convId && !payload.conversation_id) payload.conversation_id = convId;
      const response = await axios.put(`${API_URL}/api/damyxia/modules/${moduleId}`, payload);
      setModules(prev => prev.map(m => m.id === moduleId ? response.data : m));
      if (selectedModule?.id === moduleId) {
        setSelectedModule(response.data);
      }
      toast.success(t.updated);
    } catch (error) {
      console.error('Error updating module:', error);
      toast.error(t.error);
    }
  };

  // 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — Handler principal del botón.
  // Llama /api/review/audit-structured → JSON con status + errors + warnings +
  // improvements. Abre el modal maestro.
  const handleRunAudit = useCallback(async () => {
    if (auditLoading) return;
    const mod = selectedModule;
    if (!mod) {
      toast.warning(language === 'es'
        ? 'Seleccioná primero un trabajo para auditar'
        : 'Select a work to audit first');
      return;
    }
    setAuditLoading(true);
    setCertifiedPerfect(false);
    try {
      const response = await axios.post(
        `${API_URL}/api/review/audit-structured`,
        {
          module_id: mod.id,
          module_name: mod.name || 'unnamed',
          module_type: mod.type || 'custom',
          code: mod.code || '',
          language,
        },
      );
      setAuditResult(response.data);
      setShowAuditModal(true);
      // 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — persistir en audit_history.
      // No blocking (fire-and-forget) para no demorar el modal.
      axios.post(`${API_URL}/api/review/audit-save`, {
        module_id: mod.id,
        status: response.data.status,
        summary: response.data.summary || '',
        errors: response.data.errors || [],
        warnings: response.data.warnings || [],
        improvements: response.data.improvements || [],
        applied_fix: false,
      }).catch((e) => console.warn('audit-save failed:', e?.message));
      // Notificar a la vista Mis Trabajos para refrescar badges.
      try { window.dispatchEvent(new CustomEvent('damyxia-audit-updated', { detail: { moduleId: mod.id } })); } catch (e) { void e; }
    } catch (err) {
      console.error('Error running audit:', err);
      toast.error(language === 'es'
        ? 'No pudimos ejecutar la auditoría. Intentá de nuevo.'
        : 'Could not run the audit. Please try again.');
    } finally {
      setAuditLoading(false);
    }
  }, [auditLoading, selectedModule, language]);

  // Paso 1: usuario aprieta "Aceptar Corregir con IA" → DamyxIA aplica fixes.
  const handleAcceptFixWithAI = useCallback(async () => {
    if (!selectedModule || !auditResult) return;
    setAutofixLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/review/autofix`,
        {
          module_id: selectedModule.id,
          module_name: selectedModule.name || 'unnamed',
          module_type: selectedModule.type || 'custom',
          code: selectedModule.code || '',
          audit: auditResult,
          language,
        },
      );
      setAutofixPreview(response.data);
      setDiffConfirmStep(0);
      setShowDiffModal(true);
    } catch (err) {
      console.error('Error running autofix:', err);
      toast.error(language === 'es'
        ? 'DamyxIA no pudo generar la corrección. Intentá de nuevo.'
        : 'DamyxIA could not generate the fix. Please try again.');
    } finally {
      setAutofixLoading(false);
    }
  }, [selectedModule, auditResult, language]);

  // Paso 2 + 3: doble confirmación → PUT del módulo → re-auditar → certificar.
  const handleConfirmApplyFix = useCallback(async () => {
    if (!selectedModule || !autofixPreview) return;
    if (diffConfirmStep === 0) {
      setDiffConfirmStep(1); // primera confirmación
      return;
    }
    setDiffConfirmStep(2);
    try {
      // Persistir el nuevo code
      const updated = await axios.put(
        `${API_URL}/api/damyxia/modules/${selectedModule.id}`,
        { code: autofixPreview.fixed_code },
      );
      setModules((prev) => prev.map((m) => (m.id === selectedModule.id ? updated.data : m)));
      setSelectedModule(updated.data);
      // Re-auditar para certificar
      const recheck = await axios.post(
        `${API_URL}/api/review/audit-structured`,
        {
          module_id: updated.data.id,
          module_name: updated.data.name || 'unnamed',
          module_type: updated.data.type || 'custom',
          code: updated.data.code || '',
          language,
        },
      );
      setAuditResult(recheck.data);
      setShowDiffModal(false);
      setAutofixPreview(null);
      setDiffConfirmStep(0);
      // 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — persistir el resultado post-fix
      // con applied_fix=true. Historial: 2 registros (original + post-fix).
      axios.post(`${API_URL}/api/review/audit-save`, {
        module_id: updated.data.id,
        status: recheck.data.status,
        summary: recheck.data.summary || '',
        errors: recheck.data.errors || [],
        warnings: recheck.data.warnings || [],
        improvements: recheck.data.improvements || [],
        applied_fix: true,
      }).catch((e) => console.warn('audit-save (post-fix) failed:', e?.message));
      try { window.dispatchEvent(new CustomEvent('damyxia-audit-updated', { detail: { moduleId: updated.data.id } })); } catch (e) { void e; }
      if (recheck.data?.status === 'perfect') {
        setCertifiedPerfect(true);
        toast.success(language === 'es'
          ? 'Proyecto certificado ⭐⭐⭐ PERFECTO'
          : 'Project certified ⭐⭐⭐ PERFECT');
      } else {
        toast.warning(language === 'es'
          ? 'Fixes aplicados — aún quedan puntos por revisar.'
          : 'Fixes applied — some items still remain.');
      }
    } catch (err) {
      console.error('Error applying fix:', err);
      toast.error(language === 'es'
        ? 'No pudimos aplicar la corrección. El código original NO fue modificado.'
        : 'Could not apply the fix. Original code was NOT modified.');
      setDiffConfirmStep(0);
    }
  }, [selectedModule, autofixPreview, diffConfirmStep, language]);

  const handleCancelDiff = useCallback(() => {
    setShowDiffModal(false);
    setAutofixPreview(null);
    setDiffConfirmStep(0);
  }, []);

  // el módulo actual es una copia de muestra y si tiene cambios sin guardar.
  const isSampleCopyModule = useCallback((mod) => {
    return !!(mod && sampleCopyIdsRef.current.has(mod.id));
  }, []);
  const isDirtySampleCopy = useCallback((mod) => {
    if (!mod || !sampleCopyIdsRef.current.has(mod.id)) return false;
    const snap = sampleCopySnapshotsRef.current.get(mod.id);
    return (mod.code || '') !== (snap ?? '');
  }, []);

  // Cierre final tras confirmar la salida de una copia (o directamente si
  // no había cambios). kind='back' vuelve a la lista, 'workspace' cierra
  // todo el Workspace y llama a onNavigateToChat.
  const finalizeClose = useCallback((kind) => {
    closeReasonRef.current = 'user-close';
    if (kind === 'workspace') {
      setShowCreateModal(false);
      setShowCodeModal(false);
      setSelectedModule(null);
      onNavigateToChat?.();
    } else {
      setSelectedModule(null);
    }
  }, [onNavigateToChat]);

  // Intenta cerrar el módulo seleccionado. Si es una copia de muestra con
  // cambios sin guardar, abre el ExitConfirmModal con las 3 opciones.
  const attemptCloseSelectedModule = useCallback((kind = 'back') => {
    const mod = selectedModule;
    if (mod && isDirtySampleCopy(mod)) {
      setPendingCloseKind(kind);
      setShowExitModal(true);
      return;
    }
    // Si es sample-copy limpia (sin cambios), limpiamos el tracking y cerramos.
    if (mod && isSampleCopyModule(mod)) {
      sampleCopyIdsRef.current.delete(mod.id);
      sampleCopySnapshotsRef.current.delete(mod.id);
    }
    finalizeClose(kind);
  }, [selectedModule, isDirtySampleCopy, isSampleCopyModule, finalizeClose]);

  // Handler "Guardar y salir" para copias de muestra editadas: renombra la
  // copia como "Copia de {name}" si aún no lo es, refresca la lista y cierra.
  const handleSaveSampleCopyAndExit = useCallback(async () => {
    const mod = selectedModule;
    if (!mod) return;
    const alreadyCopy = /^(copia de|copy of)\s+/i.test(String(mod.name || ''));
    try {
      if (!alreadyCopy) {
        const prefix = language === 'es' ? 'Copia de' : 'Copy of';
        const newName = `${prefix} ${mod.name}`;
        const response = await axios.put(
          `${API_URL}/api/damyxia/modules/${mod.id}`,
          { name: newName }
        );
        setModules(prev => prev.map(m => m.id === mod.id ? response.data : m));
      }
      sampleCopyIdsRef.current.delete(mod.id);
      sampleCopySnapshotsRef.current.delete(mod.id);
      setShowExitModal(false);
      const kind = pendingCloseKind || 'back';
      setPendingCloseKind(null);
      try { window.dispatchEvent(new CustomEvent('damyxia-my-works-refresh')); } catch { /* noop */ }
      toast.success(language === 'es' ? 'Copia guardada en Mis Trabajos' : 'Copy saved to My Works');
      await loadModules();
      finalizeClose(kind);
    } catch (err) {
      console.error('Error saving sample copy:', err);
      toast.error(language === 'es' ? 'Error al guardar la copia' : 'Failed to save copy');
    }
  }, [selectedModule, language, pendingCloseKind, loadModules, finalizeClose]);

  // Handler "Salir sin guardar" para copias de muestra: DELETE del módulo.
  const handleDiscardSampleCopyAndExit = useCallback(async () => {
    const mod = selectedModule;
    if (!mod) return;
    try {
      const convId = (typeof window !== 'undefined' && window.__damyxia_active_conversation_id) || null;
      const q = convId ? `?conversation_id=${encodeURIComponent(convId)}` : '';
      await axios.delete(`${API_URL}/api/damyxia/modules/${mod.id}${q}`);
      setModules(prev => prev.filter(m => m.id !== mod.id));
      sampleCopyIdsRef.current.delete(mod.id);
      sampleCopySnapshotsRef.current.delete(mod.id);
      setShowExitModal(false);
      const kind = pendingCloseKind || 'back';
      setPendingCloseKind(null);
      try { window.dispatchEvent(new CustomEvent('damyxia-my-works-refresh')); } catch { /* noop */ }
      toast.success(language === 'es' ? 'Copia descartada' : 'Copy discarded');
      finalizeClose(kind);
    } catch (err) {
      console.error('Error discarding sample copy:', err);
      toast.error(language === 'es' ? 'Error al descartar' : 'Discard failed');
    }
  }, [selectedModule, language, pendingCloseKind, finalizeClose]);

  // Estado para confirmacion de borrado con doble-clic (anti window.confirm
  // que en mobile/algunos navegadores se bloquea silenciosamente).
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const pendingDeleteTimerRef = useRef(null);

  // Eliminar módulo (con confirmacion doble-clic 4s).
  const handleDelete = async (moduleId) => {
    // 1er clic: arma. 2do clic en 4s: ejecuta.
    if (pendingDeleteId !== moduleId) {
      setPendingDeleteId(moduleId);
      toast.warning(
        language === 'es'
          ? 'Pulsa "Eliminar" otra vez en 4s para confirmar.'
          : 'Click "Delete" again within 4s to confirm.'
      );
      if (pendingDeleteTimerRef.current) clearTimeout(pendingDeleteTimerRef.current);
      pendingDeleteTimerRef.current = setTimeout(() => setPendingDeleteId(null), 4000);
      return;
    }
    if (pendingDeleteTimerRef.current) clearTimeout(pendingDeleteTimerRef.current);
    setPendingDeleteId(null);

    try {
      const convId = (typeof window !== 'undefined' && window.__damyxia_active_conversation_id) || null;
      const q = convId ? `?conversation_id=${encodeURIComponent(convId)}` : '';
      await axios.delete(`${API_URL}/api/damyxia/modules/${moduleId}${q}`);
      setModules(prev => prev.filter(m => m.id !== moduleId));
      if (selectedModule?.id === moduleId) {
        closeReasonRef.current = 'deleted';
        setSelectedModule(null);
      }
      toast.success(t.deleted);
    } catch (error) {
      console.error('Error deleting module:', error);
      toast.error(t.error);
    }
  };

  // Solicitar edición con DamyxIA
  const handleRequestEdit = (module) => {
    if (onNavigateToChat) {
      onNavigateToChat({
        action: 'edit_module',
        moduleId: module.id,
        moduleName: module.name,
        currentCode: module.code
      });
    } else {
      toast.info(language === 'es' 
        ? `Ve al chat y escribe: "edita el trabajo ${module.name}: [tu instrucción]"`
        : `Go to chat and type: "edit work ${module.name}: [your instruction]"`
      );
    }
  };

  // Filtrar módulos

  return (
    <div className="h-full flex flex-col bg-slate-900" data-testid="internal-workspace">
      {/* Premium CTA Banner — solo visible si usePremiumCTA detecta enganche
         (free + first_action + free_used >= 10) y no fue cerrado esta sesión. */}
      {shouldShowPremiumCTA && !ctaClosed && (
        <PremiumCTABanner onClose={dismissPremiumCTA} />
      )}
      {/* Header — 🟥 CUADRO NEGRO 2026-06-30: altura reducida para ganar
          área útil en el Workspace (py-3→py-1.5, mb-3→mb-1, botones py-2→py-1). */}
      <div className="px-4 py-1.5 border-b border-slate-700">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2 min-w-0">
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-white truncate leading-tight">{t.title}</h1>
              <p className="text-sm text-slate-400 truncate leading-tight">{t.subtitle}</p>
            </div>
            {onNavigateToChat && (
              <button
                onClick={() => {
                  // 🟥 CUADRO NEGRO 2026-06-30 — Si el módulo abierto es una
                  // copia de muestra con cambios, primero preguntar
                  // Guardar/Descartar antes de cerrar el Workspace.
                  if (selectedModule && isDirtySampleCopy(selectedModule)) {
                    setPendingCloseKind('workspace');
                    setShowExitModal(true);
                    return;
                  }
                  // 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Si hay edit/creation modal
                  // abierto en el workspace, abrir el ExitConfirmModal universal.
                  // Si no hay modales abiertos, cerrar directo (sec 4 Workspace → Genio).
                  if (showCreateModal || showCodeModal || selectedModule) {
                    setPendingCloseKind('workspace');
                    setShowExitModal(true);
                  } else {
                    onNavigateToChat();
                  }
                }}
                title={language === 'es' ? 'Cerrar Área Interna de Trabajo' : 'Close Internal Workspace'}
                aria-label={language === 'es' ? 'Cerrar Área Interna de Trabajo' : 'Close Internal Workspace'}
                data-testid="close-workspace-btn"
                className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:text-white hover:bg-slate-700/70 transition-colors"
              >
                <X className="w-4 h-4" strokeWidth={2.25} />
              </button>
            )}
          </div>
          {/* 🟥 Grupo de acciones: D de M + P de M + Nuevo Trabajo */}
          <div className="flex items-center gap-2">
            {/* 🟥 CUADRO NEGRO 2026-06-03 — Botón T de M (Trabajos de Muestras).
                Va a la IZQUIERDA del botón P de M, según orden del owner. */}
            <button
              type="button"
              onClick={() => setShowSampleDashboards(true)}
              title={language === 'es' ? 'Trabajos de Muestras' : 'Sample Works'}
              aria-label={language === 'es' ? 'Trabajos de Muestras' : 'Sample Works'}
              data-testid="ddm-open-btn"
              className="inline-flex items-center px-3 py-1.5 h-8 text-xs font-semibold tracking-wide text-white bg-blue-500 hover:bg-blue-400 border border-blue-400/60 rounded-lg transition-colors"
            >
              {language === 'es' ? 'T de M' : 'S of W'}
            </button>
            {/* Botón P de M / S of T — Plantillas de Muestra (multi-idioma) */}
            <button
              type="button"
              onClick={() => setShowTemplatesPanel(true)}
              title={language === 'es' ? 'Plantillas de Muestra' : 'Sample of Templates'}
              aria-label={language === 'es' ? 'Plantillas de Muestra' : 'Sample of Templates'}
              data-testid="pdm-open-btn"
              className="inline-flex items-center px-3 py-1.5 h-8 text-xs font-semibold tracking-wide text-white bg-blue-500 hover:bg-blue-400 border border-blue-400/60 rounded-lg transition-colors"
            >
              {language === 'es' ? 'P de M' : 'S of T'}
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 h-8 text-xs font-semibold tracking-wide bg-blue-500 text-white rounded-lg hover:bg-blue-400 border border-blue-400/60 transition-colors"
              data-testid="create-module-btn"
              title={t.newWork}
            >
              <Plus className="w-3.5 h-3.5" />
              {language === 'es' ? 'Nuevo' : 'New'}
            </button>

            {/* 🟥 CUADRO NEGRO 2026-06-30 — Botón "Mis Trabajos" (Opción A).
                Ahora NAVEGA a la vista dedicada /mis-trabajos: la lista de
                trabajos ya no vive dentro del Workspace. */}
            <button
              type="button"
              onClick={() => { window.location.href = '/mis-trabajos'; }}
              title={language === 'es' ? 'Mis Trabajos' : 'My Works'}
              aria-label={language === 'es' ? 'Mis Trabajos' : 'My Works'}
              data-testid="my-works-open-btn"
              className="inline-flex items-center px-3 py-1.5 h-8 text-xs font-semibold tracking-wide text-white bg-blue-500 hover:bg-blue-400 border border-blue-400/60 rounded-lg transition-colors"
            >
              {language === 'es' ? 'Mis Trabajos' : 'My Works'}
            </button>
          </div>
        </div>

        {/* Barra de búsqueda — 🟥 CUADRO NEGRO 2026-06-30: altura reducida
            (py-2→py-1, min-h-7=28px, text-xs=12px) para ganar área útil.
            🟥 CUADRO NEGRO 2026-07-01: ancho reducido a max-w-[320px] para
            dejar lugar al botón "⭐ Auditoría Final IA" a la derecha. */}
        <div className="flex items-center gap-2">
          {/* 🟦 CUADRO NEGRO 2026-07-02 — Barra de acciones del módulo con
              scroll horizontal fino cuando el contenido excede el ancho.
              Contiene: Auditoría Final IA + Deploy. */}
          <div
            className="flex items-center gap-2 overflow-x-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-transparent"
            data-testid="workspace-action-bar"
            style={{ scrollbarWidth: 'thin' }}
          >
            <button
              type="button"
              onClick={handleRunAudit}
              disabled={auditLoading}
              data-testid="audit-ai-btn"
              className="inline-flex items-center whitespace-nowrap px-3 py-1.5 h-8 text-xs font-semibold tracking-wide text-white bg-blue-500 hover:bg-blue-400 border border-blue-400/60 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-wait"
              title={language === 'es' ? 'Auditar el proyecto seleccionado con Lyon IA' : 'Audit the selected project with Lyon AI'}
            >
              {auditLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  {language === 'es' ? 'Analizando…' : 'Analyzing…'}
                </>
              ) : (
                language === 'es' ? '⭐ Auditoría Final IA' : '⭐ Health Check IA'
              )}
            </button>
            {/* 🟥 P26.40 · Deploy REAL: mismo motor que el botón del panel y
                que el Deploy de la app generada (enlace propio + subdominio). */}
            <button
              type="button"
              onClick={() => setShowDeploy(true)}
              disabled={!selectedModule || auditLoading}
              data-testid="workspace-deploy-btn"
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 h-8 text-xs font-semibold tracking-wide text-slate-950 bg-cyan-400 hover:bg-cyan-300 rounded-lg transition-colors disabled:opacity-50"
              title={language === 'es' ? 'Publicar el proyecto con su enlace propio' : 'Publish the project with its own link'}
            >
              <Rocket className="w-3.5 h-3.5" />
              Deploy
            </button>
          </div>
        </div>

        {/* 🟩 P26.30 · Plantillas rápidas — arranque de un clic. */}
        <div
          className="flex items-center gap-2 overflow-x-auto"
          data-testid="quick-templates-bar"
          style={{ scrollbarWidth: 'thin' }}
        >
          <span className="text-[11px] uppercase tracking-widest text-slate-500 whitespace-nowrap">
            {language === 'es' ? 'Arranque rápido' : 'Quick start'}
          </span>
          {[
            { key: 'landing', es: 'Landing', en: 'Landing' },
            { key: 'panel', es: 'Panel', en: 'Dashboard' },
            { key: 'tienda', es: 'Tienda', en: 'Store' },
          ].map((tpl) => (
            <button
              key={tpl.key}
              type="button"
              onClick={() => handleQuickTemplate(tpl.key)}
              disabled={creatingTemplate === tpl.key}
              data-testid={`quick-template-${tpl.key}-btn`}
              title={language === 'es'
                ? `Crear una app de ${tpl.es.toLowerCase()} al instante`
                : `Create a ${tpl.en.toLowerCase()} app instantly`}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 h-8 text-xs font-semibold rounded-lg border transition-colors bg-slate-800/80 text-slate-200 border-slate-700 hover:bg-indigo-500/20 hover:border-indigo-400/60 disabled:opacity-50"
            >
              {creatingTemplate === tpl.key
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Sparkles className="w-3.5 h-3.5 text-indigo-300" />}
              {language === 'es' ? tpl.es : tpl.en}
            </button>
          ))}
          {/* 🟥 2026-02-06 · Botón "Revertir" amarillo — reubicado al lado
              de "Tienda". Dispara `damyxia:revert-both` para que ChatPanel
              ejecute AMBOS undos en paralelo (chat turn + módulo). Además
              refresca localmente el selectedModule cuando existe. */}
          <button
            type="button"
            data-testid="workspace-undo-btn"
            onClick={async () => {
              // 1) Disparar handler unificado del ChatPanel (undo chat + undo módulo en paralelo)
              try {
                window.dispatchEvent(new CustomEvent('damyxia:revert-both'));
              } catch (_) { /* noop */ }
              // 2) Refrescar el módulo local si está seleccionado, para que
              // la vista previa refleje el nuevo estado tras el undo del backend.
              if (selectedModule?.id) {
                try {
                  // Pequeño delay para que el backend termine el undo.
                  await new Promise((r) => setTimeout(r, 400));
                  const r = await axios.get(
                    `${API_URL}/api/damyxia/modules/${selectedModule.id}`,
                    { withCredentials: true },
                  );
                  if (r?.data) setSelectedModule(r.data);
                } catch (_) { /* silent — el toast del handler ya informa */ }
              }
            }}
            title={language === 'es'
              ? 'Revertir el último mensaje del chat Y la última edición visual (en paralelo)'
              : 'Undo the last chat message AND the last visual edit (in parallel)'}
            className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 h-8 text-xs font-semibold rounded-lg border transition-colors bg-amber-500/10 text-amber-400 border-amber-500/40 hover:bg-amber-500/20"
          >
            <ChevronRight className="w-3.5 h-3.5 rotate-180" />
            {language === 'es' ? 'Revertir' : 'Undo'}
          </button>
        </div>
      </div>

      {/* Contenido principal — Master/detail pattern.
         Mobile (<md): se muestra LISTA si no hay seleccion, PANEL si hay seleccion.
         Desktop (≥md): ambos lado a lado con splitter arrastrable. */}
      {/* 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — El panel izquierdo de
          trabajos, el splitter y la lista fueron ELIMINADOS. El Workspace es un
          solo cuadro central que muestra SIEMPRE el trabajo activo; la lista y
          la selección viven en la vista dedicada /mis-trabajos. */}
      <div className="flex-1 flex overflow-hidden">
        {/* Panel de detalle.
           Mobile: full-width visible SOLO cuando hay modulo seleccionado.
           Desktop: flex-1 siempre visible. SIN padding interior para que
           el cuadro de Vista Previa pegue a los bordes del split y se
           extienda hasta el fondo. min-h-0 + min-w-0 para evitar que la
           cadena flex colapse la altura/ancho. */}
        <div className="flex flex-1 flex-col overflow-hidden min-w-0 min-h-0" data-testid="workspace-single-pane">
          {/* Boton "Volver a la lista" — solo en mobile. */}
          {selectedModule && (
            <div className="flex items-center gap-2 mb-2">
              <button
                onClick={() => attemptCloseSelectedModule('back')}
                data-testid="workspace-mobile-back-btn"
                className="md:hidden inline-flex items-center gap-1.5 self-start px-2.5 py-1.5 rounded-lg text-xs text-slate-300 bg-slate-800/50 hover:bg-slate-700/60 transition-colors"
              >
                <ChevronRight className="w-3.5 h-3.5 rotate-180" />
                {language === 'es' ? 'Volver' : 'Back'}
              </button>
              {/* 🟥 2026-02-06 · El botón "Revertir" amarillo se MOVIÓ al
                  lado del botón "Tienda" en el arranque rápido, para que
                  funcione paralelo al botón "Revertir" del chat. Ver el
                  bloque quick-templates-bar arriba. */}
            </div>
          )}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            <ModuleDetailPanel
              module={selectedModule}
              onUpdate={handleUpdate}
              onClose={() => attemptCloseSelectedModule('back')}
              onRequestEdit={handleRequestEdit}
              onChangeType={(m) => {
                // Si el modulo no es Premium y type_locked: bloquear con modal Premium.
                if (m.type_locked && !(quotaStatus && quotaStatus.is_premium)) {
                  setLockedTypeModalModule(m);
                  setShowLockedTypeModal(true);
                  return;
                }
                setLockedTypeModalModule(m);
                setShowLockedTypeModal(true);
              }}
              quotaStatus={quotaStatus}
              onViewCode={(module) => {
                setCodeModalModule(module);
                setShowCodeModal(true);
              }}
            />
          </div>
        </div>
      </div>

      {/* Modal de creación */}
      <CreateModuleModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreate}
      />

      {/* 🟥 Panel "P de M" — Plantillas de Muestra */}
      <SystemTemplatesPanel
        open={showTemplatesPanel}
        onClose={() => setShowTemplatesPanel(false)}
        language={language}
      />

      {/* 🟥 Panel "D de M" — Dashboards de Muestra (read-only, 32 muestras del owner). */}
      <SampleDashboardsPanel
        open={showSampleDashboards}
        onClose={() => setShowSampleDashboards(false)}
        language={language}
      />

      {/* 🟥 CUADRO NEGRO 2026-06 · WORKSPACE ÚNICO — El panel "Mis Trabajos"
          (lista dentro del Workspace) fue ELIMINADO: vive en /mis-trabajos. */}

      {/* 🟥 P26.40 · Deploy real del proyecto seleccionado. */}
      {showDeploy && selectedModule && (
        <Suspense fallback={null}>
          <DeployModal
            module={selectedModule}
            language={language}
            onClose={() => setShowDeploy(false)}
          />
        </Suspense>
      )}

      {/* 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — Modal principal "Auditoría Final IA".
          Renderiza el resultado estructurado del backend (status + errors +
          warnings + improvements). Ofrece "Aceptar Corregir con IA" (si hay
          errores o warnings) y "Rechazar". Al aceptar dispara autofix + diff. */}
      {showAuditModal && auditResult && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3"
          data-testid="audit-modal"
          onClick={() => setShowAuditModal(false)}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con badge de estado grande */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/70">
              <div className="min-w-0 flex items-center gap-3">
                {(() => {
                  const st = certifiedPerfect ? 'perfect' : (auditResult.status || 'perfect');
                  const badge = st === 'errors'
                    ? { label: '🟥 ERRORES', cls: 'bg-red-600/30 text-red-200 border-red-500/60' }
                    : st === 'warnings'
                      ? { label: '🟨 ADVERTENCIAS', cls: 'bg-yellow-600/30 text-yellow-200 border-yellow-500/60' }
                      : { label: '🟩 PERFECTO ⭐⭐⭐', cls: 'bg-emerald-600/30 text-emerald-200 border-emerald-500/60' };
                  return (
                    <span
                      data-testid={`audit-status-${st}`}
                      className={`px-3 py-1 rounded-full border text-sm font-bold tracking-wide ${badge.cls}`}
                    >
                      {badge.label}
                    </span>
                  );
                })()}
                <div className="min-w-0">
                  <h2 className="text-white font-semibold truncate">
                    {selectedModule?.name}
                  </h2>
                  <p className="text-xs text-slate-400 mt-0.5 truncate">
                    {auditResult.summary || '—'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAuditModal(false)}
                data-testid="audit-modal-close-btn"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
                aria-label={language === 'es' ? 'Cerrar' : 'Close'}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Cuerpo: errores + warnings + improvements */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {['errors', 'warnings', 'improvements'].map((key) => {
                const items = auditResult[key] || [];
                if (items.length === 0) return null;
                const palette = key === 'errors'
                  ? { title: language === 'es' ? '🟥 Errores' : '🟥 Errors', border: 'border-red-500/60', bg: 'bg-red-950/30', label: 'text-red-300' }
                  : key === 'warnings'
                    ? { title: language === 'es' ? '🟨 Advertencias' : '🟨 Warnings', border: 'border-yellow-500/60', bg: 'bg-yellow-950/30', label: 'text-yellow-300' }
                    : { title: language === 'es' ? '💡 Mejoras sugeridas (max 3)' : '💡 Suggested improvements (max 3)', border: 'border-blue-500/60', bg: 'bg-blue-950/30', label: 'text-blue-300' };
                return (
                  <div key={key} className={`rounded-xl border ${palette.border} ${palette.bg} p-3`}>
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-2 ${palette.label}`}>
                      {palette.title} ({items.length})
                    </p>
                    <ul className="space-y-2">
                      {items.map((it, idx) => (
                        <li key={idx} className="text-sm text-slate-200" data-testid={`audit-${key}-item-${idx}`}>
                          <div className="flex items-baseline gap-2">
                            {it.component && <span className="text-xs text-slate-400">{it.component}</span>}
                            {it.line != null && <span className="text-[10px] text-slate-500">L{it.line}</span>}
                          </div>
                          <p className="font-medium">{it.message}</p>
                          {it.suggestion && <p className="text-xs text-slate-400 mt-0.5">→ {it.suggestion}</p>}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
              {(auditResult.errors || []).length === 0 && (auditResult.warnings || []).length === 0 && (auditResult.improvements || []).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-8">
                  {language === 'es'
                    ? 'Sin observaciones. El proyecto está en perfecto estado.'
                    : 'No observations. The project is in perfect state.'}
                </p>
              )}
            </div>

            {/* Footer con acciones */}
            <div className="px-5 py-3 border-t border-slate-700/70 flex items-center justify-end gap-2">
              {(auditResult.status === 'errors' || auditResult.status === 'warnings') && !certifiedPerfect && (
                <>
                  <button
                    onClick={() => setShowAuditModal(false)}
                    data-testid="audit-reject-btn"
                    className="px-4 py-1.5 text-sm font-semibold text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    {language === 'es' ? 'Rechazar' : 'Reject'}
                  </button>
                  <button
                    onClick={handleAcceptFixWithAI}
                    disabled={autofixLoading}
                    data-testid="audit-accept-fix-btn"
                    className="inline-flex items-center px-4 py-1.5 text-sm font-semibold text-white bg-blue-500 hover:bg-blue-400 rounded-lg transition-colors disabled:opacity-60"
                  >
                    {autofixLoading && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                    {language === 'es' ? 'Aceptar Corregir con IA' : 'Accept Fix with AI'}
                  </button>
                </>
              )}
              {(auditResult.status === 'perfect' || certifiedPerfect) && (
                <button
                  onClick={() => setShowAuditModal(false)}
                  data-testid="audit-close-perfect-btn"
                  className="px-4 py-1.5 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-500 rounded-lg transition-colors"
                >
                  {language === 'es' ? 'Cerrar' : 'Close'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — Modal Diff Preview + doble confirmación */}
      {showDiffModal && autofixPreview && selectedModule && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3"
          data-testid="diff-modal"
        >
          <div className="w-full max-w-4xl max-h-[92vh] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-700/70">
              <div className="min-w-0">
                <h2 className="text-white font-semibold">
                  {language === 'es' ? 'Diff Preview — DamyxIA' : 'Diff Preview — DamyxIA'}
                </h2>
                <p className="text-xs text-slate-400 mt-0.5 truncate">
                  {autofixPreview.applied_notes}
                </p>
              </div>
              <button
                onClick={handleCancelDiff}
                data-testid="diff-cancel-x-btn"
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Diff render */}
            <div className="flex-1 overflow-auto p-4">
              <DiffView original={selectedModule.code || ''} fixed={autofixPreview.fixed_code || ''} />
            </div>

            <div className="px-5 py-3 border-t border-slate-700/70 flex items-center justify-between gap-2">
              <p className="text-xs text-slate-400">
                {diffConfirmStep === 0
                  ? (language === 'es' ? 'Revisá el diff. Al aceptar, se pedirá una segunda confirmación.' : 'Review the diff. You will be asked to confirm twice.')
                  : diffConfirmStep === 1
                    ? (language === 'es' ? '⚠️ Confirmá NUEVAMENTE para sobrescribir el código original.' : '⚠️ Confirm AGAIN to overwrite the original code.')
                    : (language === 'es' ? 'Aplicando…' : 'Applying…')}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCancelDiff}
                  data-testid="diff-cancel-btn"
                  disabled={diffConfirmStep === 2}
                  className="px-3 py-1.5 text-sm font-semibold text-slate-300 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-60"
                >
                  {language === 'es' ? 'Cancelar' : 'Cancel'}
                </button>
                <button
                  onClick={handleConfirmApplyFix}
                  disabled={diffConfirmStep === 2}
                  data-testid="diff-confirm-btn"
                  className={`inline-flex items-center px-4 py-1.5 text-sm font-semibold text-white rounded-lg transition-colors disabled:opacity-60 ${
                    diffConfirmStep === 1 ? 'bg-red-600 hover:bg-red-500 animate-pulse' : 'bg-blue-500 hover:bg-blue-400'
                  }`}
                >
                  {diffConfirmStep === 2 && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  {diffConfirmStep === 0
                    ? (language === 'es' ? 'Aplicar cambios' : 'Apply changes')
                    : diffConfirmStep === 1
                      ? (language === 'es' ? 'Confirmar sobrescritura' : 'Confirm overwrite')
                      : (language === 'es' ? 'Aplicando…' : 'Applying…')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de código — lazy-loaded. Solo se monta cuando el usuario abre
          "Ver Código" (showCodeModal=true). Mientras el chunk descarga, el
          fallback es null porque el modal ocupa el viewport completo y no
          tiene sentido pintar un loader detrás del backdrop. */}
      {showCodeModal && (
        <Suspense fallback={null}>
          <CodeViewModal
            isOpen={showCodeModal}
            onClose={() => {
              setShowCodeModal(false);
              setCodeModalModule(null);
            }}
            module={codeModalModule}
            onSave={handleUpdate}
          />
        </Suspense>
      )}


      {/* Modal para cambiar tipo base (regla 4 — solo via boton explicito). */}
      <ChangeTypeModal
        isOpen={showLockedTypeModal}
        module={lockedTypeModalModule}
        quotaStatus={quotaStatus}
        onClose={() => {
          setShowLockedTypeModal(false);
          setLockedTypeModalModule(null);
        }}
        onConfirm={async (newType) => {
          if (!lockedTypeModalModule) return;
          try {
            await handleChangeType(lockedTypeModalModule.id, newType);
            setShowLockedTypeModal(false);
            setLockedTypeModalModule(null);
          } catch (_) {
            // El error ya se mostro como toast.
          }
        }}
      />

      {/* Upsell modal — solo en momentos de friccion alta (1 vez por sesion por tipo). */}
      <WorkspaceUpsellModal
        isOpen={!!upsellTrigger}
        trigger={upsellTrigger}
        onClose={() => setUpsellTrigger(null)}
      />

      {/* 🟦 CUADRO NEGRO #1 FASE 3 sec 3.2 — Modal X universal en Workspace.
          Se abre cuando hay edit/creation modal abierto o cuando se cierra
          una copia de muestra con cambios. En modo "copia editable" ofrece
          Guardar y salir (PUT rename) + Salir sin guardar (DELETE). */}
      <ExitConfirmModal
        open={showExitModal}
        onCancel={() => {
          setShowExitModal(false);
          setPendingCloseKind(null);
        }}
        onSaveAndExit={
          selectedModule && isSampleCopyModule(selectedModule)
            ? handleSaveSampleCopyAndExit
            : () => {
                // 🟥 Trabajos Custom / no-sample: las ediciones se persisten
                // en tiempo real vía handleUpdate (PUT /api/damyxia/modules/{id}).
                // "Guardar y salir" cierra el workspace sin descartar nada.
                const kind = pendingCloseKind || 'workspace';
                setShowExitModal(false);
                setPendingCloseKind(null);
                finalizeClose(kind);
              }
        }
        onExitNoSave={
          selectedModule && isSampleCopyModule(selectedModule)
            ? handleDiscardSampleCopyAndExit
            : () => {
                const kind = pendingCloseKind || 'workspace';
                setShowExitModal(false);
                setPendingCloseKind(null);
                finalizeClose(kind);
              }
        }
        es={language === 'es'}
        title={selectedModule && isSampleCopyModule(selectedModule)
          ? (language === 'es' ? '¿Guardar la copia en Mis Trabajos?' : 'Save the copy to My Works?')
          : undefined}
        description={selectedModule && isSampleCopyModule(selectedModule)
          ? (language === 'es'
              ? 'Estás cerrando una copia de una muestra con cambios. Podés guardarla en "Mis Trabajos" o descartarla (se borra del backend).'
              : 'You are closing a sample copy with unsaved changes. You can save it into "My Works" or discard it (it will be deleted from the backend).')
          : (language === 'es'
              ? '¿Cerrar el Workspace y volver al Genio? Los cambios sin guardar se perderán.'
              : 'Close the Workspace and return to the Genie? Unsaved changes will be lost.')}
        testIdPrefix="workspace-exit"
      />
    </div>
  );
};

// 🟥 CUADRO NEGRO MAESTRO 2026-07-01 — Componente DiffView.
// Renderiza diff por líneas (added=verde, removed=rojo, unchanged=neutro)
// usando la lib `diff` (jsdiff v9). Formato monospace, similar a GitHub.
const DiffView = ({ original, fixed }) => {
  const chunks = useMemo(() => {
    try {
      return diffLines(String(original || ''), String(fixed || ''));
    } catch { return []; }
  }, [original, fixed]);
  const stats = useMemo(() => {
    let added = 0, removed = 0;
    for (const c of chunks) {
      const lines = String(c.value || '').split('\n').filter((l) => l.length > 0).length;
      if (c.added) added += lines;
      else if (c.removed) removed += lines;
    }
    return { added, removed };
  }, [chunks]);
  return (
    <div data-testid="diff-view">
      <div className="mb-2 text-xs text-slate-400 flex items-center gap-3">
        <span className="text-emerald-400">+{stats.added}</span>
        <span className="text-red-400">-{stats.removed}</span>
      </div>
      <pre className="rounded-lg bg-slate-950 border border-slate-800 overflow-x-auto text-xs">
        {chunks.map((c, idx) => {
          const cls = c.added
            ? 'bg-emerald-900/40 text-emerald-100'
            : c.removed
              ? 'bg-red-900/40 text-red-100'
              : 'text-slate-300';
          const prefix = c.added ? '+ ' : c.removed ? '- ' : '  ';
          return (
            <div key={idx} className={cls}>
              {String(c.value || '').split('\n').map((line, li, arr) => {
                if (li === arr.length - 1 && line === '') return null;
                return (
                  <div key={li} className="px-3 py-0.5 font-mono whitespace-pre">
                    <span className="opacity-60 select-none">{prefix}</span>{line}
                  </div>
                );
              })}
            </div>
          );
        })}
      </pre>
    </div>
  );
};

export default InternalWorkspace;
